import { type BooleanInput } from '@angular/cdk/coercion';
import { Directive, booleanAttribute, input, output } from '@angular/core';
import { injectUiAccordionConfig } from './accordion.config';
import type { UiAccordionType, UiOrientation } from './accordion.interface';
import { provideUiAccordionState, uiAccordionState } from './accordion.state';

/**
 * Apply the `uiAccordion` directive to an element that represents the group of accordion items.
 */
@Directive({
    selector: '[uiAccordion]',
    exportAs: 'uiAccordion',
    providers: [provideUiAccordionState()],
    host: {
        '[attr.data-orientation]': 'state.orientation()',
        '[attr.data-disabled]': 'state.disabled() ? "" : null'
    }
})
export class UiAccordionDirective<T> {
    /**
     * Access the global accordion configuration
     */
    private readonly config = injectUiAccordionConfig();

    /**
     * The type of the accordion
     */
    readonly type = input<UiAccordionType>(this.config.type, {
        alias: 'uiAccordionType'
    });

    /**
     * Whether the accordion is collapsible
     */
    readonly collapsible = input<boolean, BooleanInput>(this.config.collapsible, {
        alias: 'uiAccordionCollapsible',
        transform: booleanAttribute
    });

    /**
     * The value of the accordion
     */
    readonly value = input<T | T[] | null>(null, {
        alias: 'uiAccordionValue'
    });

    /**
     * Event emitted when the accordion value changes
     */
    readonly valueChange = output<T | T[] | null>({
        alias: 'uiAccordionValueChange'
    });

    /**
     * Whether the accordion is disabled
     */
    readonly disabled = input<boolean, BooleanInput>(false, {
        alias: 'uiAccordionDisabled',
        transform: booleanAttribute
    });

    /**
     * The accordion orientation
     */
    readonly orientation = input<UiOrientation>(this.config.orientation, {
        alias: 'uiAccordionOrientation'
    });

    /**
     * The accordion state.
     */
    private readonly state = uiAccordionState<UiAccordionDirective<T>>(this);

    /**
     * @param value The value to check
     * @returns Whether the value is open
     */
    isOpen(value: T): boolean {
        if (this.state.type() === 'multiple') {
            return (this.state.value() as T[] | null)?.includes(value) ?? false;
        }

        return this.state.value() === value;
    }

    toggle(value: T): void {
        const isOpen = this.isOpen(value);

        // if we are in single mode and the value is already open and the accordion is not collapsible, do nothing
        if (this.state.type() === 'single' && isOpen && !this.state.collapsible()) {
            return;
        }

        // if we are in single mode then toggle the value
        if (this.state.type() === 'single') {
            this.state.value.set(isOpen ? null : value);
            this.valueChange.emit(this.state.value());

            return;
        }

        // if we are in multiple mode then toggle the value
        const values = (this.state.value() as T[]) ?? [];

        if (isOpen) {
            this.state.value.set(values.filter(v => v !== value));
        } else {
            this.state.value.set([...values, value]);
        }

        this.valueChange.emit(this.state.value());
    }
}
